import { useState } from "react";
import { cn } from "@/lib/utils";
import type { InboxMessage } from "@/hooks/use-inbox-conversation";
import type { InboxConversationSummary } from "@/hooks/use-inbox-conversations";
import { Button } from "@/components/ui/button";
import { ExternalLink, Film, Image as ImageIcon, Mic, MessageSquare, RefreshCcw } from "lucide-react";
import { useRetryInboxMessage } from "@/hooks/use-retry-inbox-message";
import { useMembers, type BrandRole } from "@/hooks/use-members";
import {
  useAssignConversation,
  useSetConversationStatus,
} from "@/hooks/use-inbox-conversation-actions";
import { useBrandContextOptional } from "@/contexts/brand-context";
import { useTranslation } from "react-i18next";
import { PlatformIcon, getPlatformLabel } from "@/components/ui/platform-icon";
import { useLocale, formatDate } from "@/i18n/format";
import { INBOX_WRITE_ROLES } from "@/lib/inbox-roles";

const ASSIGN_ROLES: ReadonlySet<BrandRole> = new Set([
  "OWNER",
  "ADMIN",
  "CREATOR",
  "REVIEWER",
]);

interface Props {
  conversation: InboxConversationSummary;
  messages: InboxMessage[];
  hasOlder?: boolean;
  isFetchingOlder?: boolean;
  onLoadOlder?: () => void;
}

function dayKey(iso: string): string {
  return new Date(iso).toDateString();
}

function timeOnly(iso: string, locale: string): string {
  return new Intl.DateTimeFormat(locale, { hour: "numeric", minute: "2-digit" }).format(new Date(iso));
}

function TemplateBlock({ template }: { template: NonNullable<InboxMessage["template"]> }) {
  return (
    <div className="flex flex-col gap-2">
      {template.elements.map((el, i) => (
        <div key={i} className="overflow-hidden rounded-lg border border-border bg-background">
          {el.imageUrl && (
            <img src={el.imageUrl} alt="" className="max-h-48 w-full object-cover" />
          )}
          {(el.title || el.subtitle) && (
            <div className="px-3 py-2">
              {el.title && (
                <div className="whitespace-pre-wrap break-words font-medium">{el.title}</div>
              )}
              {el.subtitle && (
                <div className="mt-0.5 whitespace-pre-wrap break-words text-[12px] text-muted-foreground">
                  {el.subtitle}
                </div>
              )}
            </div>
          )}
          {el.buttons.map((b, j) => {
            const cls =
              "flex items-center justify-center gap-1 border-t border-border px-3 py-1.5 text-[12px] font-medium text-primary";
            return b.url ? (
              <a key={j} href={b.url} target="_blank" rel="noopener noreferrer" className={cn(cls, "hover:bg-surface-2")}>
                {b.title} <ExternalLink size={11} />
              </a>
            ) : (
              <div key={j} className={cls}>
                {b.title}
              </div>
            );
          })}
        </div>
      ))}
    </div>
  );
}

function AttachmentBlock({ message, t }: { message: InboxMessage; t: (k: string) => string }) {
  const type = message.attachmentType;
  if (message.template?.elements.length) return <TemplateBlock template={message.template} />;
  if (!message.attachmentUrl && !type) return null;

  if (type === "image" && message.attachmentUrl) {
    return (
      <img
        src={message.attachmentUrl}
        alt={t("thread.attachment")}
        className="rounded-lg max-w-full max-h-64 object-cover"
      />
    );
  }
  if (type === "video" && message.attachmentUrl) {
    return (
      <video
        src={message.attachmentUrl}
        controls
        className="rounded-lg max-w-full max-h-64"
      />
    );
  }
  if (type === "audio" && message.attachmentUrl) {
    return <audio src={message.attachmentUrl} controls className="max-w-full" />;
  }
  if (type === "story_reply") {
    return (
      <div className="flex items-center gap-2 rounded-md border border-border bg-surface-2 px-3 py-2 text-[12px] text-muted-foreground">
        <MessageSquare size={14} /> {t("thread.storyReply")}
      </div>
    );
  }
  // Generic placeholder for unknown / file types
  const Icon = type === "video" ? Film : type === "audio" ? Mic : ImageIcon;
  return (
    <div className="flex items-center gap-2 rounded-md border border-border bg-surface-2 px-3 py-2 text-[12px] text-muted-foreground">
      <Icon size={14} /> {type ?? t("thread.attachment")}
    </div>
  );
}

export function ConversationThread({
  conversation,
  messages,
  hasOlder,
  isFetchingOlder,
  onLoadOlder,
}: Props) {
  const { t } = useTranslation("inbox");
  const locale = useLocale();

  // Sort ascending (oldest first) for chronological top→bottom rendering.
  const sorted = [...messages].sort((a, b) => {
    const ad = new Date(a.platformSentAt ?? a.sentAt).getTime();
    const bd = new Date(b.platformSentAt ?? b.sentAt).getTime();
    return ad - bd;
  });

  // Group by day
  const groups: Array<{ key: string; messages: InboxMessage[] }> = [];
  for (const m of sorted) {
    const k = dayKey(m.platformSentAt ?? m.sentAt);
    const last = groups[groups.length - 1];
    if (last && last.key === k) last.messages.push(m);
    else groups.push({ key: k, messages: [m] });
  }

  const brandCtx = useBrandContextOptional();
  const role = (brandCtx?.currentUserRole as BrandRole | undefined) ?? "VIEWER";
  const canAssign = ASSIGN_ROLES.has(role);
  const retryMut = useRetryInboxMessage();
  const membersQuery = useMembers(canAssign ? conversation.brandId : "");
  const assignMut = useAssignConversation(conversation.brandId, conversation.id);
  const statusMut = useSetConversationStatus(conversation.brandId, conversation.id);
  const [snoozeOpen, setSnoozeOpen] = useState(false);
  const [snoozeDate, setSnoozeDate] = useState("");

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between gap-3 border-b border-border px-4 py-3 flex-shrink-0">
        <div className="flex items-center gap-3 min-w-0">
          <span className="w-8 h-8 rounded-full bg-brand-subtle text-brand-subtle-text flex items-center justify-center text-xs font-semibold flex-shrink-0">
            {(conversation.participantName ?? conversation.participantId).charAt(0).toUpperCase()}
          </span>
          <div className="min-w-0">
            <div className="text-[13px] font-semibold truncate">
              {conversation.participantName ?? conversation.participantId}
            </div>
            <div className="flex items-center gap-1 text-[11px] text-muted-foreground">
              <PlatformIcon platform={conversation.platform} size={12} />
              {conversation.socialAccount?.platformUsername
                ? `@${conversation.socialAccount.platformUsername}`
                : getPlatformLabel(conversation.platform)}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 text-[11px]">
          {canAssign ? (
            <select
              className="rounded-md border border-border bg-surface-1 px-2 py-1 text-[11px]"
              value={conversation.status}
              onChange={(e) => {
                const v = e.target.value as "OPEN" | "SNOOZED" | "DONE" | "ARCHIVED";
                if (v === "SNOOZED") {
                  setSnoozeOpen(true);
                } else {
                  statusMut.mutate({ status: v });
                }
              }}
            >
              <option value="OPEN">{t("status.OPEN")}</option>
              <option value="SNOOZED">{t("status.SNOOZED")}</option>
              <option value="DONE">{t("status.DONE")}</option>
              <option value="ARCHIVED">{t("status.ARCHIVED")}</option>
            </select>
          ) : (
            <span className="rounded-md border border-border bg-surface-1 px-2 py-1 text-muted-foreground">
              {t(`status.${conversation.status}`, { defaultValue: conversation.status })}
            </span>
          )}
          {canAssign ? (
            <select
              className="rounded-md border border-border bg-surface-1 px-2 py-1 text-[11px]"
              value={conversation.assignedUserId ?? ""}
              onChange={(e) => assignMut.mutate(e.target.value || null)}
            >
              <option value="">{t("assignee.unassigned")}</option>
              {(membersQuery.data ?? []).map((m) => (
                <option key={m.userId} value={m.userId}>
                  {m.name ?? m.email}
                </option>
              ))}
            </select>
          ) : (
            <span className="rounded-md border border-border bg-surface-1 px-2 py-1 text-muted-foreground">
              {conversation.assignedUser
                ? t("assignee.assigned", {
                    name: conversation.assignedUser.name ?? conversation.assignedUser.email,
                  })
                : t("assignee.unassigned")}
            </span>
          )}
        </div>
      </div>

      {snoozeOpen && (
        <div className="flex items-center gap-2 border-b border-border bg-surface-1 px-4 py-2 text-[12px]">
          <label className="text-muted-foreground">{t("snooze.label")}</label>
          <input
            type="datetime-local"
            value={snoozeDate}
            onChange={(e) => setSnoozeDate(e.target.value)}
            className="rounded-md border border-input bg-background px-2 py-1 text-[12px]"
          />
          <Button
            size="sm"
            disabled={!snoozeDate}
            onClick={() => {
              const iso = new Date(snoozeDate).toISOString();
              statusMut.mutate(
                { status: "SNOOZED", snoozedUntil: iso },
                { onSuccess: () => setSnoozeOpen(false) },
              );
            }}
          >
            {t("snooze.confirm")}
          </Button>
          <Button size="sm" variant="ghost" onClick={() => setSnoozeOpen(false)}>
            {t("snooze.cancel")}
          </Button>
        </div>
      )}

      {/* Messages */}
      <div className="flex-1 overflow-y-auto px-4 py-4">
        {hasOlder && (
          <div className="flex justify-center mb-3">
            <Button
              variant="outline"
              size="sm"
              disabled={isFetchingOlder}
              onClick={onLoadOlder}
            >
              {isFetchingOlder ? t("thread.loading") : t("thread.loadOlder")}
            </Button>
          </div>
        )}
        {groups.map((g) => (
          <div key={g.key} className="mb-4">
            <div className="text-center text-[11px] text-muted-foreground mb-2">
              {formatDate(
                new Date(g.messages[0].platformSentAt ?? g.messages[0].sentAt),
                locale,
                { month: "short", day: "numeric", year: "numeric" },
              )}
            </div>
            <div className="flex flex-col gap-2">
              {g.messages.map((m) => (
                <div
                  key={m.id}
                  className={cn(
                    "flex",
                    m.direction === "out" ? "justify-end" : "justify-start",
                  )}
                >
                  <div
                    className={cn(
                      "max-w-[75%] rounded-2xl px-3 py-2 text-[13px]",
                      m.direction === "out"
                        ? "bg-primary text-primary-foreground"
                        : "bg-surface-2 text-foreground",
                      m.deliveryStatus === "FAILED" && "border border-destructive",
                      m.deliveryStatus === "PENDING" && "opacity-70",
                    )}
                  >
                    {m.attachmentUrl || m.attachmentType || m.template ? (
                      <div className="mb-1">
                        <AttachmentBlock message={m} t={t} />
                      </div>
                    ) : null}
                    {m.text && <div className="whitespace-pre-wrap break-words">{m.text}</div>}
                    <div
                      className={cn(
                        "mt-0.5 text-[10px] flex items-center gap-2",
                        m.direction === "out"
                          ? "text-primary-foreground/70 justify-end"
                          : "text-muted-foreground",
                      )}
                    >
                      <span>{timeOnly(m.platformSentAt ?? m.sentAt, locale)}</span>
                      {m.sentByAi && (
                        <span
                          title={t("thread.sentByAi")}
                          className="rounded-sm border border-current/30 px-1 text-[9px] font-semibold tracking-wide"
                        >
                          AI
                        </span>
                      )}
                      {m.deliveryStatus === "FAILED" && (
                        <>
                          <span className="text-destructive">· {t("thread.failedToSend")}</span>
                          {INBOX_WRITE_ROLES.has(role) && (
                            <button
                              type="button"
                              onClick={() =>
                                retryMut.mutate({
                                  brandId: conversation.brandId,
                                  conversationId: conversation.id,
                                  messageId: m.id,
                                })
                              }
                              disabled={retryMut.isPending}
                              className="inline-flex items-center gap-1 underline hover:no-underline disabled:opacity-50"
                            >
                              <RefreshCcw size={10} /> {t("thread.retry")}
                            </button>
                          )}
                        </>
                      )}
                      {m.deliveryStatus === "PENDING" && <span>· {t("thread.sending")}</span>}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
